import React, { useMemo, useCallback } from 'react';
import {
  type TagActionsContext,
  TAG_ACTION_MODE,
  TAG_OPERATION_TYPE,
  type TagOperationType,
  type TagActionMode,
} from '@scale/genai-compass-shared/cards/tag_actions';

import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import {
  Box,
  Code,
  DataTable,
  Flex,
  FormField,
  Icon,
  Tabs,
  Text,
  Tooltip,
} from '@scale/scaleui-radix';
import MultiSelect from 'corp/components/UI/Select';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { useCompassCard } from '@scale/compass-ui/store';
import { columnsFromRecord } from '@scale/compass-shared/utils';
import { faInfoCircle, faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { createColumnHelper } from '@tanstack/react-table';

type TagActionsTabs = TagActionMode;

const TagActionsCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const { card, updateCardContext } = useCompassCard(index);
  const context = card.defaultContext as TagActionsContext;
  const { retrievedOutput: priorCardRetrievedOutput } = useCompassCard(index - 1);

  const currentTab = useMemo(() => context.mode || TAG_ACTION_MODE.STANDARD, [context.mode]);

  const updateContext = useCallback(
    (updates: Partial<TagActionsContext>) => {
      updateCardContext({ ...context, ...updates });
    },
    [context, updateCardContext],
  );

  const loadTags = useCallback(async (search: string) => {
    try {
      const query = { search };
      const result = await apiFetch(null, 'corp-api/tags', { query });
      const options = result.tags.map((tag: { _id: string; name: string }) => ({
        value: tag._id,
        label: tag.name,
      }));
      return { options };
    } catch (error) {
      return { options: [] };
    }
  }, []);

  // Validate required columns - require exact "userId" column name
  const { userIdCol, error: columnError } = useMemo(() => {
    try {
      const columns = columnsFromRecord(priorCardRetrievedOutput?.response?.data);

      // Look for exact "userId" or "user_id" column name (case-insensitive)
      const userIdCol = columns.find(
        col => col.header.toLowerCase() === 'userid' || col.header.toLowerCase() === 'user_id',
      );

      if (!userIdCol) {
        return {
          userIdCol: undefined,
          error: `No userId column found. Please name your user ID column exactly 'userId' or 'user_id'.`,
        };
      }

      return {
        userIdCol,
        error: null,
      };
    } catch (error) {
      return {
        userIdCol: undefined,
        error: 'Error processing columns from prior card.',
      };
    }
  }, [priorCardRetrievedOutput]);

  const handleTabChange = useCallback(
    (tab: TagActionsTabs) => {
      updateContext({
        mode: tab,

        ...(tab === TAG_ACTION_MODE.DYNAMIC && { tagIds: [] }),
      });
    },
    [updateContext],
  );

  // Show error if validation fails - block UI like TaskActions
  if (columnError) {
    return (
      <div className="flex flex-row gap-36 justify-between">
        <SanitizedMarkdown text={columnError} />
      </div>
    );
  }

  const standardContent = (
    <div className="flex flex-row gap-36 justify-between">
      <div>
        <SanitizedMarkdown
          text={`**This will apply the selected tag operation to all users in the prior card.**\nAn async corp job will be kicked off upon submission.`}
        />
      </div>
      <div className="flex flex-col gap-4 min-w-[400px]">
        {/* User ID Column Check */}
        <div className="flex justify-end">
          <div
            className={`flex items-center gap-2 ${
              userIdCol ? 'text-neutral-600' : 'text-gray-400'
            }`}
          >
            <Icon
              icon={userIdCol ? faCheckCircle : faInfoCircle}
              size="sm"
              label="Info"
              className={userIdCol ? 'text-primary-600' : 'text-gray-400'}
            />
            <Text as="p" size="2">
              {userIdCol ? (
                <>
                  Using column <Code>{userIdCol.header}</Code>&nbsp;to identify User ID
                </>
              ) : (
                'No userId column found'
              )}
            </Text>
          </div>
        </div>

        {/* Operation Type */}
        <FormField.Root size="2">
          <FormField.Label htmlFor="standard-operation-type" required>
            Action to Apply to All Users
          </FormField.Label>
          <MultiSelect
            className="w-full rounded-lg"
            label="Select operation"
            multi={false}
            loadOptions={async () => ({
              options: [
                { value: TAG_OPERATION_TYPE.ADD_TAGS, label: 'Add Tags' },
                { value: TAG_OPERATION_TYPE.REMOVE_TAGS, label: 'Remove Tags' },
              ],
            })}
            value={
              context.operationType
                ? {
                    value: context.operationType,
                    label:
                      context.operationType === TAG_OPERATION_TYPE.ADD_TAGS
                        ? 'Add Tags'
                        : 'Remove Tags',
                  }
                : null
            }
            onChange={(selectedOption: { value: string; label: string } | null) => {
              if (selectedOption) {
                updateContext({ operationType: selectedOption.value as TagOperationType });
              }
            }}
            placeholder="Select operation"
            isSearchable={false}
            isClearable={false}
            styles={{
              container: (provided: any) => ({
                ...provided,
                width: '100%',
              }),
            }}
          />
        </FormField.Root>

        {/* Tag Selection */}
        <FormField.Root size="2">
          <FormField.Label htmlFor="standard-tags" required>
            Select Tags to{' '}
            {context.operationType === TAG_OPERATION_TYPE.ADD_TAGS ? 'Add to' : 'Remove from'} Users
          </FormField.Label>
          <div className="w-full">
            <MultiSelect
              className="w-full rounded-lg"
              label="Select tags"
              multi={true}
              loadOptions={loadTags}
              value={context.selectedTags || []}
              onChange={(selectedOptions: Array<{ value: string; label: string }> | null) => {
                const tagIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                const selectedTags = selectedOptions || [];
                updateContext({ tagIds, selectedTags });
              }}
              placeholder={`Select tags to ${
                context.operationType === TAG_OPERATION_TYPE.ADD_TAGS ? 'add' : 'remove'
              }`}
              isSearchable={true}
              isClearable={true}
              styles={{
                container: (provided: any) => ({
                  ...provided,
                  width: '100%',
                }),
                menu: (provided: any) => ({
                  ...provided,
                  maxWidth: '400px',
                }),
                valueContainer: (provided: any) => ({
                  ...provided,
                  flexWrap: 'wrap',
                  maxHeight: '120px',
                  overflowY: 'auto',
                }),
              }}
            />
          </div>
        </FormField.Root>
      </div>
    </div>
  );

  const dynamicContent = (
    <div className="flex flex-row gap-36 justify-between">
      <div className="w-full">
        <SanitizedMarkdown
          text={`**This will apply a custom tag to each user, row-wise. Ensure the following columns are present in the prior card:**`}
        />

        <div className="mt-4 mb-4">
          <ParametersTable />
        </div>

        <SanitizedMarkdown text={`An async corp job will be kicked off upon submission.`} />
      </div>

      <div className="flex flex-col gap-4 min-w-[400px]">
        {/* Operation Type for Dynamic Mode */}
        <FormField.Root size="2">
          <FormField.Label htmlFor="dynamic-operation-type" required>
            Action to Apply Per Row
          </FormField.Label>
          <MultiSelect
            className="w-full rounded-lg"
            label="Select operation"
            multi={false}
            loadOptions={async () => ({
              options: [
                { value: TAG_OPERATION_TYPE.ADD_TAGS, label: 'Add Tags' },
                { value: TAG_OPERATION_TYPE.REMOVE_TAGS, label: 'Remove Tags' },
              ],
            })}
            value={
              context.operationType
                ? {
                    value: context.operationType,
                    label:
                      context.operationType === TAG_OPERATION_TYPE.ADD_TAGS
                        ? 'Add Tags'
                        : 'Remove Tags',
                  }
                : null
            }
            onChange={(selectedOption: { value: string; label: string } | null) => {
              if (selectedOption) {
                updateContext({ operationType: selectedOption.value as TagOperationType });
              }
            }}
            placeholder="Select operation"
            isSearchable={false}
            isClearable={false}
            styles={{
              container: (provided: any) => ({
                ...provided,
                width: '100%',
              }),
            }}
          />
        </FormField.Root>
      </div>
    </div>
  );

  return (
    <Tabs.Root value={currentTab} onValueChange={value => handleTabChange(value as TagActionsTabs)}>
      <Tabs.List>
        <Tabs.Trigger value={TAG_ACTION_MODE.STANDARD}>
          <Flex direction="row" gap="2">
            Standard
            <Tooltip content="Apply the same tag operation to all users in the userId column">
              <Icon icon={faInfoCircle} label="info" size={1} />
            </Tooltip>
          </Flex>
        </Tabs.Trigger>
        <Tabs.Trigger value={TAG_ACTION_MODE.DYNAMIC}>
          <Flex direction="row" gap="2">
            Dynamic
            <Tooltip content="Apply custom tag operations per row based on userId and tagId columns">
              <Icon icon={faInfoCircle} label="info" size={1} />
            </Tooltip>
          </Flex>
        </Tabs.Trigger>
      </Tabs.List>
      <Box pt="4">
        <Tabs.Content value={TAG_ACTION_MODE.STANDARD}>{standardContent}</Tabs.Content>
        <Tabs.Content value={TAG_ACTION_MODE.DYNAMIC}>{dynamicContent}</Tabs.Content>
      </Box>
    </Tabs.Root>
  );
};

const ParametersTable = () => {
  const paramMetadata = useMemo(
    () => ({
      userId: {
        description: 'The user id to apply the tag operation to (must be named exactly "userId")',
        required: true,
      },
      tagId: {
        description: 'The tag id to add or remove for this user (must be named exactly "tagId")',
        required: true,
      },
    }),
    [],
  );

  // Generate table data from param metadata
  const data = useMemo(
    () =>
      Object.entries(paramMetadata).map(([columnName, { description, required }]) => ({
        columnName,
        description: required ? `(Required) - ${description}` : `(Optional) - ${description}`,
      })),
    [paramMetadata],
  );

  const columnHelper = createColumnHelper<{ columnName: string; description: string }>();

  const ColumnNameCell = ({ value }: { value: string }) => <Code>{value}</Code>;
  ColumnNameCell.displayName = 'ColumnNameCell';

  const DescriptionCell = ({ value }: { value: string }) => <>{value}</>;
  DescriptionCell.displayName = 'DescriptionCell';

  const renderColumnName = (info: any) => <ColumnNameCell value={info.getValue()} />;
  renderColumnName.displayName = 'renderColumnName';

  const renderDescription = (info: any) => <DescriptionCell value={info.getValue()} />;
  renderDescription.displayName = 'renderDescription';

  const columns = [
    columnHelper.accessor('columnName', {
      id: 'columnName',
      header: 'Column Name',
      cell: renderColumnName,
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: 'Description',
      cell: renderDescription,
    }),
  ];

  return (
    <DataTable.Root
      columns={columns}
      data={data}
      enableSorting={false}
      enableColumnFilters={false}
      initialState={{
        pagination: {
          pageIndex: 0,
          pageSize: 10,
        },
      }}
    >
      {({ table }) => (
        <DataTable.Table>
          <DataTable.Header>
            {table.getHeaderGroups().map(headerGroup => (
              <DataTable.HeaderGroup key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <DataTable.ColumnHeaderCell key={header.id} header={header} />
                ))}
              </DataTable.HeaderGroup>
            ))}
          </DataTable.Header>
          <DataTable.Body>
            {table.getRowModel().rows.map(row => (
              <DataTable.Row key={row.id} row={row}>
                {row.getVisibleCells().map(cell => (
                  <DataTable.Cell key={cell.id} cell={cell} />
                ))}
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable.Table>
      )}
    </DataTable.Root>
  );
};

ParametersTable.displayName = 'ParametersTable';

export default React.memo(TagActionsCardComponent);
